import React, { Fragment, useState, useReducer } from 'react';
import get from 'lodash/get';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import dateTimeFormat from 'legacy-src/utility/DateTime';
import LearnosityProvider from 'legacy-src/utility/LearnosityProvider';
import SingleStudentPerformanceSummary from 'legacy/src/components/assessment/Results/SingleStudentPerformanceSummary';

const reducer = (state = [], action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update-other-parts-results':
      let newState = state ? [...state] : [];
      newState.push(payload.otherPartResults);
      return newState;
    default:
      throw Error('action type is not supported');
  }
};

const SingleStudentQuestions = (props) => {
  const [otherPartsResultsData, setOtherPartsResultsData] = useReducer(
    reducer,
    null,
  );
  const [otherPartsResultsByItemData, setOtherPartResultsByItem] =
    useState(null);
  const [
    otherPartsResponseByAnalysisData,
    setOtherPartsResponseByAnalysisData,
  ] = useState(null);

  const {
    tagPairsData,
    params,
    teacherOtherParts,
    submissionData,
    assignment,
    routes,
  } = props;
  const { assignmentProgressWithTagPairs } = tagPairsData;
  const { otherParts } = assignmentProgressWithTagPairs;
  const {
    ppcPartId,
    assignmentId,
    studentId: currentStudentId,
    questionId,
  } = params;
  const currentPartId = Number.parseInt(ppcPartId, 10);
  const currentAssignmentId = Number.parseInt(assignmentId, 10);

  const isOtherPart = currentPartId !== currentAssignmentId;

  let associatedPart;
  let hasPartAssignment = true;
  if (isOtherPart) {
    associatedPart = otherParts.find(
      (otherPart) => otherPart.assignmentId === currentPartId,
    );
    if (!associatedPart) {
      associatedPart = teacherOtherParts.find(
        (otherPart) => otherPart.assignmentId === currentPartId,
      );
      hasPartAssignment = false;
    }
  }

  const updateOtherPartResultsByItem = (data) => {
    const newOtherPartResultsByItem = otherParts.map((otherPart) => {
      const { assignmentId, learnosityActivityId } = otherPart;

      const resultsByItemData = data.find((resultsByItem) => {
        const report = resultsByItem.data;
        const reportKeys = Object.keys(report);

        return reportKeys.includes(learnosityActivityId);
      });

      return {
        assignmentId,
        learnosityActivityId,
        resultsByItemData: [resultsByItemData],
      };
    });

    setOtherPartResultsByItem(newOtherPartResultsByItem);
  };

  const updateOtherPartResults = (data) => {
    let otherPartResults;
    otherParts.forEach((otherPart) => {
      const { assignmentId, learnosityActivityId } = otherPart;

      const resultsData = data.find((results) => {
        const report = results.data;
        const reportKeys = Object.keys(report);

        return reportKeys.includes(learnosityActivityId);
      });

      if (resultsData) {
        otherPartResults = {
          assignmentId,
          learnosityActivityId,
          resultsData: [resultsData],
        };

        setOtherPartsResultsData({
          type: 'update-other-parts-results',
          payload: {
            otherPartResults,
          },
        });
      }
    });
  };

  const updateOtherPartResponseByAnalysis = (data) => {
    const learnosityActivityIds = otherParts.map(
      (otherPart) => otherPart.learnosityActivityId,
    );
    const report = data[0];
    const { sessions } = report.data;
    const sessionsKeys = Object.keys(sessions);
    const otherPartSession = sessionsKeys.find((sessionsKey) =>
      learnosityActivityIds.includes(sessions[sessionsKey].activity_id),
    );
    const associatedPart = otherParts.find(
      (otherPart) =>
        otherPart.learnosityActivityId ===
        sessions[otherPartSession].activity_id,
    );
    const { assignmentId, learnosityActivityId } = associatedPart;

    setOtherPartsResponseByAnalysisData((oldState) => [
      ...(oldState || []),
      {
        assignmentId,
        learnosityActivityId,
        responseByAnalysisData: [report],
      },
    ]);
  };

  const {
    otherPartsResults,
    otherPartsResultsByItem,
    otherPartsResponseByAnalysis,
  } = assignmentProgressWithTagPairs;

  /**
   * Get results for other parts.
   * */
  if (isOtherPart && currentPartId) {
    let reports = [];
    if (questionId) {
      if (
        questionId &&
        otherPartsResponseByAnalysis &&
        otherPartsResponseByAnalysis.length &&
        otherPartsResponseByAnalysisData === null
      ) {
        /*
         * if we try and JSON.parse an otherPartsResponseByAnalysis array with more than one object
         * we get an "Uncaught SyntaxError: Unexpected token , in JSON at position X" exception and
         * user gets a blank page. to get around this, we parse only the object with the key of the currentPart
         */
        const partAnalysis = otherPartsResponseByAnalysis.filter(
          (analysis) => JSON.parse(analysis)[currentPartId] !== undefined,
        );
        const initOptions = !!partAnalysis.length
          ? JSON.parse(partAnalysis[0])[currentPartId]
          : false;
        !!partAnalysis.length &&
          reports.push(
            <LearnosityProvider
              containerElement="learnosity_report_response_analysis"
              apiType="reports"
              initOptions={JSON.parse(initOptions)}
              dataListener={(data) => updateOtherPartResponseByAnalysis(data)}
            />,
          );
      }
    }

    if (
      otherPartsResults &&
      otherPartsResults.length > 0 &&
      otherPartsResultsData === null
    ) {
      otherPartsResults.forEach((initOptions) => {
        reports.push(
          <LearnosityProvider
            containerElement="learnosity_report_results"
            apiType="reports"
            initOptions={JSON.parse(initOptions)}
            dataListener={(data) => updateOtherPartResults(data)}
          />,
        );
      });
    }

    if (otherPartsResultsByItem && otherPartsResultsByItemData === null) {
      reports.push(
        <LearnosityProvider
          containerElement="learnosity_report_results_by_item"
          apiType="reports"
          initOptions={JSON.parse(otherPartsResultsByItem)}
          dataListener={(data) => updateOtherPartResultsByItem(data)}
        />,
      );
    }

    if (reports.length) {
      return (
        <Fragment>
          <LoadingSpinner />
          {reports}
        </Fragment>
      );
    }
  }

  let childProps = {
    ...props,
    /**
     * Teacher with student context has to have class performance
     * */
    isYourResult: false,
  };

  let learnosityReportsData;
  let timer;
  let timeTaken;
  if (isOtherPart) {
    /**
     * Overriding for student context
     * PPC do not have FRQs so this can be nullified
     * */
    const learnosityReportsByItemData =
      (otherPartsResultsByItemData || []).find(({ assignmentId }) => {
        return assignmentId === currentPartId;
      }) || {};
    const learnosityReportsResponseAnalysisData =
      (otherPartsResponseByAnalysisData || []).find(({ assignmentId }) => {
        return assignmentId === currentPartId;
      }) || {};

    childProps = {
      ...childProps,
      hasPartAssignment,
      teacherOtherParts,
      isOtherPart,
      learnosityReportsByRubricData: null,
      questions: associatedPart.questions,
      learnosityReportsByItemData:
        learnosityReportsByItemData.resultsByItemData || [],
      learnosityReportsResponseAnalysisData:
        learnosityReportsResponseAnalysisData.responseByAnalysisData || [
          {
            data: {},
          },
        ],
    };

    learnosityReportsData = (
      (otherPartsResultsData || []).find(({ assignmentId }) => {
        return assignmentId === currentPartId;
      }) || {}
    ).resultsData;

    ({ timer, timeTaken } = associatedPart);
  } else {
    ({
      learnosityReportsData,
      assignment: { timer, timeTaken },
    } = childProps);
  }

  const studentTimeTaken =
    timeTaken.find(({ studentId }) => studentId === currentStudentId) || {};
  const timeTakenData = {
    timer,
    timeTaken: Math.floor((studentTimeTaken.seconds || 0) / 60),
  };

  const movedStudent = submissionData.find(
    (submission) =>
      submission.user_id.toString() === currentStudentId.toString() &&
      submission.is_copied === true,
  );
  const submission = get(movedStudent, 'submitted', null);
  const submissionDate = submission
    ? dateTimeFormat(submission, 'dateShort', assignment.timezone)
    : null;
  const showStudentMovedMessage = movedStudent !== undefined;
  const studentMovedMessage = showStudentMovedMessage && (
    <div className="SingleStudentSummaryByPart--moved-student-message">
      Assignment completed in previous class section on {submissionDate}
    </div>
  );

  const currentRoute = routes[routes.length - 1];
  // getting the child this way because for some reason, I am getting the parent component as the child
  const { component: Component } = currentRoute;

  return (
    <Fragment>
      <SingleStudentPerformanceSummary
        learnosityReportsData={learnosityReportsData}
        studentId={currentStudentId}
        timeTakenData={timeTakenData}
        showStudentMovedMessage={showStudentMovedMessage}
        studentMovedMessage={studentMovedMessage}
      />
      <Component {...childProps} />
    </Fragment>
  );
};

SingleStudentQuestions.propTypes = {};

export default SingleStudentQuestions;
